import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Lock, ArrowLeft } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import PublicNavbar from '../../components/public/navbar/PublicNavbar';
import PublicFooter from '../../components/public/footer/PublicFooter';
import { useCart } from '../../context/CartContext';
import { createBooking } from '../../services/bookings';
import { sendBookingEmails } from '../../services/email';
import { checkCartAvailability } from '../../services/availability';
import { STRIPE_PUBLISHABLE_KEY } from '../../config/stripe';
import './Checkout.css';

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#ffffff',
      fontFamily: 'Inter, sans-serif',
      fontSize: '16px',
      '::placeholder': {
        color: '#737373',
      },
    },
    invalid: {
      color: '#ef4444',
    },
  },
};

const CheckoutForm = () => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { cart, getCartTotal, clearCart, calculateDays } = useCart();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const sendConfirmationEmail = async (bookingData, bookingId) => {
    try {
      console.log('🔵 Sending emails...');
      const result = await sendBookingEmails(bookingData, bookingId);
      if (!result.success) {
        console.warn('⚠️ Email notification failed, but booking was successful');
      } else {
        console.log('✅ Emails sent successfully');
      }
    } catch (error) {
      console.error('❌ Error sending emails:', error);
      // Don't fail the booking if email fails
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('🔵 Checkout started');

    if (!stripe || !elements) {
      console.error('❌ Stripe not loaded');
      return;
    }

    if (!formData.name || !formData.email) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('🔵 Creating payment method...');
      
      // Get card element
      const cardElement = elements.getElement(CardElement);

      // Create payment method
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        },
      });

      if (stripeError) {
        console.error('❌ Stripe error:', stripeError);
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      console.log('✅ Payment method created:', paymentMethod.id);

      // FINAL AVAILABILITY CHECK before creating booking
      console.log('🔵 Performing final availability check...');
      const availabilityCheck = await checkCartAvailability(cart);

      if (!availabilityCheck.allAvailable) {
        console.error('❌ Items no longer available:', availabilityCheck.unavailableItems);
        setError(
          `Sorry, some items are no longer available for your selected dates. Please return to cart and select different dates.`
        );
        setLoading(false);
        return;
      }

      console.log('✅ All items available, proceeding with booking...');

      // Create booking in Firebase
      const bookingData = {
        customerName: formData.name,
        customerEmail: formData.email,
        customerPhone: formData.phone,
        notes: formData.notes,
        items: cart.map(item => ({
          id: item.id,
          type: item.type,
          name: item.name,
          price: item.price,
          startDate: item.dates.start,
          endDate: item.dates.end,
          days: calculateDays(item.dates.start, item.dates.end),
          total: item.price * calculateDays(item.dates.start, item.dates.end),
        })),
        totalAmount: getCartTotal(),
        paymentMethodId: paymentMethod.id,
        paymentStatus: 'completed',
      };

      console.log('🔵 Creating booking in Firebase...', bookingData);

      const result = await createBooking(bookingData);

      console.log('🔵 Booking result:', result);

      if (result.success) {
        console.log('✅ Booking created with ID:', result.id);
        
        // Send confirmation email
        await sendConfirmationEmail(bookingData, result.id);

        // Clear cart
        clearCart();
        console.log('✅ Cart cleared');

        // Redirect to confirmation page (use full page redirect)
        console.log('🔵 Redirecting to confirmation...');
        window.location.href = `/confirmation/${result.id}`;
      } else {
        console.error('❌ Booking failed:', result.error);
        setError('Failed to create booking. Please try again.');
      }
    } catch (err) {
      console.error('❌ Checkout error:', err);
      setError('An error occurred. Please try again.');
    }

    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      {error && (
        <div className="checkout-error">
          <p>{error}</p>
        </div>
      )}

      {/* Contact Information */}
      <div className="checkout-section">
        <h3 className="checkout-section-title">Contact Information</h3>
        
        <div className="form-group">
          <label className="form-label">Full Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="form-input"
            placeholder="John Doe"
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="form-input"
              placeholder="john@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Phone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="form-input"
              placeholder="+1 (555) 123-4567"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Additional Notes</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            className="form-textarea"
            rows="3"
            placeholder="Any special requests or instructions..."
          />
        </div>
      </div>

      {/* Payment Information */}
      <div className="checkout-section">
        <h3 className="checkout-section-title">
          <Lock size={20} />
          Payment Information
        </h3>
        
        <div className="form-group">
          <label className="form-label">Card Details</label>
          <div className="card-element-container">
            <CardElement options={CARD_ELEMENT_OPTIONS} />
          </div>
          <p className="form-help">
            Your payment information is secure and encrypted
          </p>
        </div>
      </div>

      {/* Order Summary */}
      <div className="checkout-section checkout-summary-section">
        <h3 className="checkout-section-title">Order Summary</h3>
        
        <div className="summary-items">
          {cart.map((item) => {
            const days = calculateDays(item.dates.start, item.dates.end);
            const itemTotal = item.price * days;

            return (
              <div key={`${item.type}-${item.id}`} className="summary-item">
                <div className="summary-item-details">
                  <span className="summary-item-name">{item.name}</span>
                  <span className="summary-item-dates">
                    {item.dates.start} to {item.dates.end} ({days} days)
                  </span>
                </div>
                <span className="summary-item-price">${itemTotal.toFixed(2)}</span>
              </div>
            );
          })}
        </div>

        <div className="summary-total">
          <span className="summary-total-label">Total Amount</span>
          <span className="summary-total-value">${getCartTotal().toFixed(2)}</span>
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={!stripe || loading}
        className="btn btn-primary btn-lg checkout-submit"
      >
        {loading ? (
          'Processing...'
        ) : (
          <>
            <CreditCard size={20} />
            Pay ${getCartTotal().toFixed(2)}
          </>
        )}
      </button>

      <div className="checkout-secure">
        <Lock size={16} />
        <span>Secure checkout powered by Stripe</span>
      </div>
    </form>
  );
};

const Checkout = () => {
  const navigate = useNavigate();
  const { cart } = useCart();

  // Redirect if cart is empty
  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="public-checkout-page">
      <PublicNavbar />

      <div className="page-header">
        <div className="container">
          <button onClick={() => navigate('/cart')} className="back-button">
            <ArrowLeft size={20} />
            Back to Cart
          </button>
          <h1 className="page-title">Checkout</h1>
          <p className="page-subtitle">Complete your booking</p>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="checkout-container">
            <Elements stripe={stripePromise}>
              <CheckoutForm />
            </Elements>
          </div>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default Checkout;